import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiError } from "../api/errors";
import { useBackend } from "../app/BackendProvider";
import type { AppRole } from "../contract/types";
import { adminSessionStore } from "./adminSession";
import { sessionStore } from "./session";
import { onUnauthorized } from "./appGate";
import { LoginScreen } from "./LoginScreen";

/**
 * Porte d'accès applicative (login principal, contrat v1.38 §8bis — décision
 * utilisateur 2026-09-09), montée **au-dessus de toute l'application**.
 *
 * Au démarrage : `GET /auth/me`.
 *  - `200` → l'app se rend normalement, on garde le `username` pour l'en-tête.
 *  - `401` → **il faut distinguer deux situations**, car `/auth/me` répond `401`
 *    dès qu'il n'y a pas de cookie, y compris quand la porte est *désactivée*
 *    côté backend (`AUTH_ENABLED=false`, déploiement de transition) :
 *      · on sonde une route normalement gardée (`GET /networks`) ;
 *      · elle répond `401` aussi ⇒ la porte est **active** et on n'est pas
 *        connecté ⇒ écran de connexion bloquant ;
 *      · elle répond ⇒ la porte est **dormante** ⇒ on laisse passer (la prod
 *        reste utilisable pendant la fenêtre de transition).
 *  - erreur transport / `503` → on laisse passer : ne pas enfermer l'exploitant
 *    derrière un mur de connexion sur une panne réseau ; la gestion d'erreur
 *    normale des écrans prend le relais.
 *
 * En fonctionnement : tout `401` sur une route de données (relayé par
 * `onUnauthorized`, câblé dans `api/http.ts`) ⇒ session expirée / révoquée ⇒
 * retour à l'écran de connexion (brief §4).
 *
 * **`role` (contrat v1.39)** : `/auth/me` renvoie aussi `role: "app" | "admin"`.
 * `role === "admin"` ⇒ le cookie de cette porte ouvre déjà `/admin/**` — c'est
 * ce que lit `AdminScreen` pour ne PAS redemander un second login au panneau
 * d'administration quand le compte connecté ici est déjà un compte admin.
 *
 * **`assistantEnabled` (contrat v1.56)** : `/auth/me` porte aussi ce booléen —
 * ce compte a-t-il accès à l'assistant IA admin (chat consultatif branché sur
 * les données réseau) ? Dérivé côté serveur d'une liste de comptes nominatifs
 * jamais exposée au front (`config.assistant.allowedAdminUsernames`) : ne
 * JAMAIS coder un nom de compte ni `role === "admin"` seul pour décider
 * d'afficher cet écran, seul ce booléen fait foi.
 */

type GateState = "checking" | "open" | "locked";

interface AppGateValue {
  /** Nom de compte connecté, ou `null` (porte dormante : app rendue sans session). */
  username: string | null;
  /** Rôle du compte connecté (`null` : porte dormante, aucune session à lire). */
  role: AppRole | null;
  /** Accès à l'assistant IA admin (v1.56) — `false` par défaut, porte dormante incluse. */
  assistantEnabled: boolean;
  /** Déconnexion : `POST /auth/logout` puis retour à l'écran de connexion. */
  logout: () => Promise<void>;
}

const AppGateContext = createContext<AppGateValue | null>(null);

export function AppGate({ children }: { children: ReactNode }) {
  const backend = useBackend();
  const [state, setState] = useState<GateState>("checking");
  const [username, setUsername] = useState<string | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  // Anti-course : seul le dernier démarrage lancé a le droit d'écrire l'état.
  const bootId = useRef(0);

  /**
   * Renvoie une promesse résolue au dernier état RETENU (`bootId` toujours en
   * tête au moment de résoudre) — `LoginScreen` s'en sert pour savoir si la
   * reconnexion a VRAIMENT abouti après un `POST /auth/login` réussi. Sans
   * ça, un `/auth/me` qui échoue juste après (cookie non honoré côté serveur,
   * jamais observé depuis le front — `HttpOnly` — mais signalé par
   * l'utilisateur, 2026-09-20 : « je boucle sur la page de connexion »)
   * rebasculait sur un formulaire de connexion vide, SANS AUCUNE ERREUR
   * affichée : l'échec vivait dans `AppGate`, pas dans `LoginScreen`, qui
   * venait d'être démonté puis remonté à neuf. Ça ressemblait à une boucle
   * silencieuse plutôt qu'à un échec diagnosticable.
   */
  const runBoot = useCallback((): Promise<GateState> => {
    const id = ++bootId.current;
    return new Promise<GateState>((resolve) => {
      const settle = (
        next: GateState,
        name: string | null,
        nextRole: AppRole | null = null,
        nextAssistantEnabled = false,
      ) => {
        if (bootId.current === id) {
          setUsername(name);
          setRole(nextRole);
          setAssistantEnabled(nextAssistantEnabled);
          setState(next);
        }
        resolve(next);
      };

      setState("checking");
      void (async () => {
        try {
          const me = await backend.api.authMe();
          settle("open", me.username, me.role, me.assistantEnabled);
        } catch (err) {
          if (!(err instanceof ApiError) || err.httpStatus !== 401) {
            settle("open", null); // panne réseau / 503 : on ne bloque pas
            return;
          }
          // `/auth/me` = 401 : la porte est-elle vraiment active ?
          try {
            await backend.api.listNetworks();
            settle("open", null); // porte dormante (AUTH_ENABLED=false)
          } catch (probe) {
            settle(
              probe instanceof ApiError && probe.httpStatus === 401 ? "locked" : "open",
              null,
            );
          }
        }
      })();
    });
  }, [backend]);

  useEffect(() => {
    void runBoot();
  }, [runBoot]);

  useEffect(
    () =>
      onUnauthorized(() => {
        bootId.current++; // invalide un démarrage en cours
        setUsername(null);
        setRole(null);
        setAssistantEnabled(false);
        setState("locked");
      }),
    [],
  );

  const logout = useCallback(async () => {
    try {
      await backend.api.appLogout();
    } catch {
      /* le cookie est peut-être déjà mort — on bascule quand même */
    }
    sessionStore.clear();
    adminSessionStore.clear();
    bootId.current++;
    setUsername(null);
    setRole(null);
    setAssistantEnabled(false);
    setState("locked");
  }, [backend]);

  if (state === "checking") {
    return <p className="p-4 has-text-grey">Chargement…</p>;
  }
  if (state === "locked") {
    return <LoginScreen onAuthenticated={() => runBoot().then((next) => next === "open")} />;
  }
  return (
    <AppGateContext.Provider value={{ username, role, assistantEnabled, logout }}>{children}</AppGateContext.Provider>
  );
}

export function useAppGate(): AppGateValue {
  const value = useContext(AppGateContext);
  if (!value) throw new Error("useAppGate doit être utilisé sous <AppGate>");
  return value;
}

/**
 * Variante non stricte : `null` hors de `<AppGate>` plutôt qu'une exception.
 * Pour du code appelé aussi bien sous la porte (l'usage réel, `App.tsx`) que
 * monté isolément (tests d'un écran précis, ex. `AdminScreen.test.tsx`) — sans
 * ce filet, `AdminScreen` casserait toute la suite existante de ces tests, qui
 * ne montent que `<AdminScreen>` seul et n'ont pas besoin du rôle du compte de
 * poste (ils exercent le jeton admin dédié, régime indépendant).
 */
export function useOptionalAppGate(): AppGateValue | null {
  return useContext(AppGateContext);
}
